<template>
  <div
    class="fts-location location-form-group"
    :class="{'comfort-design': comfortDesign}"
  >
    <input
      type="text"
      class="location-form-control fts-form-control"
      :class="{'form-control--error': error}"
      v-model="placeName"
    />

    <font-awesome-icon
      :icon="['fas', 'location-crosshairs']"
      class="location-toggle"
      :class="{'location-toggle--active': model && model.lat && model.lng}"
      @click="setCurrentLocation(true)"
      v-if="isDetectOnly"
    />

    <font-awesome-icon
      :icon="['fas', 'map-marked-alt']"
      class="location-toggle"
      :class="{'location-toggle--active': model && model.lat && model.lng}"
      @click.stop="openModal"
      v-else
    />

    <fts-client-modal
      :append-to-body="appendToBody"
      :header="false"
      width="1080px"
      :class="referenceFor"
      @keydown.native.enter.stop
      ref="ftsClientModal"
      @closed="resetModal"
    >
      <!-- body -->
      <template #body>
        <div class="location-search">
          <fts-form-label icon="fts-icon-map-marker" placement="left" :text-wrap="false">
            {{item.displayName || 'Location'}}
          </fts-form-label>
          <gmap-autocomplete
            class="fts-form-control location-search-control"
            placeholder="Search a location"
            @input="setPlaceName"
            @place_changed="setPlace"
            ref="locationInput"
            v-if="isAutocomplete"
          />
          <input
            type="text"
            class="fts-form-control location-search-control"
            v-model="modalPlaceName"
            ref="locationInput"
            v-else
          >
          <div class="location-search-action location-search-action--times" v-if="modalPlaceName || isModalLatLng">
            <font-awesome-icon :icon="['fas', 'times']" @click="clearMarker" />
          </div>
          <div class="location-search-action location-search-action--search"  v-else>
            <font-awesome-icon :icon="['fas', 'search']" fixed-width />
          </div>
        </div>
        <gmap-map
          :center="center"
          :options="{
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
          }"
          :zoom="16"
          class="location-map-container"
          @click="setMarker($event.latLng)"
          ref="mymap"
        >
          <gmap-marker
            :clickable="true"
            :draggable="true"
            :position="marker.position"
            @click="center=marker.position"
          />
        </gmap-map>
      </template>
      <!-- footer:right -->
      <template #footerRight>
        <fts-btn text @click="closeModal">
          <font-awesome-icon :icon="['fas', 'times']" /> Cancel
        </fts-btn>

        <fts-btn color="primary" @click="closeModal(true)">
          <font-awesome-icon :icon="['fas', 'check']" /> Ok
        </fts-btn>
      </template>
    </fts-client-modal>
  </div>
</template>

<script>
import FTSClientModal from './FTSClientModal'
import FTSBtn from './FTSBtn'
import FTSFormLabel from './FTSFormLabel'

export default {
  components: {
    'fts-client-modal': FTSClientModal,
    'fts-btn': FTSBtn,
    'fts-form-label': FTSFormLabel
  },
  props: {
    appendToBody: Boolean,
    referenceFor: String,
    item: Object,
    formData: Object,
    comfortDesign: Boolean,
    error: Boolean
  },
  data: () => ({
    center: { lat: 1.3521, lng: 103.8198},
    marker: {},
    modalFormData: null
  }),
  methods: {
    setPlaceName(e) {
      this.modalPlaceName = e.target.value;
      if (!this.modalPlaceName) {
        this.clearMarker();
      }
    },
    setPlace(place) {
      if (place && place.geometry && place.geometry.location) {
        this.setMarker(place.geometry.location);
        this.center = this.marker.position;
      }
      if (this.isAutocomplete) {
        try {
          this.modalPlaceName = this.$refs.locationInput.$el.value;
        } catch(err) {
          console.warn("[DOMException]", err);
        }
      }
    },
    setMarker(location) {
      this.marker = { 
        position: {
          lat: location.lat(),
          lng: location.lng()
        } 
      };
      this.$refs.mymap.panTo(this.marker.position);
      this.modalFormData = {
        ...this.modalFormData,
        lat: this.marker.position.lat,
        lng: this.marker.position.lng
      }
    },
    clearMarker() {
      this.marker = {};
      this.modalFormData = null;
      if (this.isAutocomplete) {
        try {
          this.$refs.locationInput.$el.value = null;
        } catch(err) {
          console.warn("[DOMException]", err);
        };
      }
    },
    geolocate() {
      if (this.isModalLatLng) {
        const location = {
          lat: this.model && this.model.lat,
          lng: this.model && this.model.lng,
        };
        this.center = location;
        this.marker = { position: location };
      } else {
        navigator.geolocation.getCurrentPosition(position => {
          this.center = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
        });
      }
    },
    openModal() {
      this.modalFormData = {...this.model};
      this.geolocate();
      this.$refs.ftsClientModal.show();
      this.$nextTick(() => {
        if (this.isAutocomplete) {
          this.$refs.locationInput.$el.focus();
          if (this.modalPlaceName) {
            this.$refs.locationInput.$el.value = this.modalPlaceName;
          }
        } else {
          this.$refs.locationInput.focus();
        }
      });
    },
    closeModal(isDone) {
      if (isDone) {
        this.model = this.modalFormData;
      }
      this.$refs.ftsClientModal.close();
    },
    resetModal() {
      this.modalFormData = null;
      this.marker = {};
      this.$emit('close');
    },
    setCurrentLocation(isManual) {
      navigator.geolocation.getCurrentPosition(position => {
        let formData = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        }

        if (this.model && this.model.placeName) {
          formData.placeName = this.model.placeName;
        } else if (!!formData.lat & !!formData.lng) {
          formData.placeName = `${formData.lat}, ${formData.lng}`;
        }

        this.model = formData;

        if (isManual) {
          this.$toasted.info('Location updated.');
        }
      });
    }
  },
  computed: {
    model: {
      get() {
        return this.formData[this.item.fieldName];
      },
      set(val) {
        if (val && (val.placeName || (val.lat && val.lng))) {
          this.$set(this.formData, this.item.fieldName, val);
        } else {
          this.$delete(this.formData, this.item.fieldName);
        }
      }
    },
    placeName: {
      get() {
        return this.model && this.model.placeName;
      },
      set(val) {
        this.model = { ...this.model, placeName: val };
      }
    },
    modalPlaceName: {
      get() {
        return this.modalFormData && this.modalFormData.placeName;
      },
      set(val) {
        this.modalFormData = {
          ...this.modalFormData,
          placeName: val
        }
      }
    },
    isModalLatLng() {
      return !!(this.modalFormData && this.modalFormData.lat && this.modalFormData.lng);
    },
    isAutocomplete() {
      return this.item && this.item.autocomplete;
    },
    isDetectOnly() {
      return this.item && this.item.detectOnly;
    }
  },
  mounted() {
    if (this.isDetectOnly)
      this.setCurrentLocation();
  }
};
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;

.fts-location {
  position: relative;
  .location-toggle {
    @include absolute-center-y;
    right: 0.75rem;
    font-size: var(--inistate-label-text-14);
    color: var(--fts-default);
    cursor: pointer;
    &:hover,
    &.location-toggle--active {
      color: var(--fts-primary);
    }
  }
  .location-form-control {
    padding-right: 2.375rem;
  }
}
.location-search {
  position: relative;
  display: flex;
  margin-top: 0.25rem;
  margin-bottom: 1rem;
  .location-search-control {
    padding-right: 2rem;
  }
  .location-search-action {
    @include absolute-center-y;
    right: 0.75rem;
    font-size: 0.75rem;
    color: var(--fts-default);
    &.location-search-action--search {
      pointer-events: none;
    }
    &.location-search-action--times {
      cursor: pointer;
    }
  }
}
.location-map-container {
  width:100%;
  height: 540px;
}
</style>